import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle2, MinusCircle, AlertTriangle, Loader2 } from "lucide-react";
import { inboundLog } from "../lib/api";

const STYLE = {
  filed: { Icon: CheckCircle2, cls: "text-emerald-600", label: "Filed" },
  skipped: { Icon: MinusCircle, cls: "text-stone-400", label: "Skipped" },
  bounced: { Icon: AlertTriangle, cls: "text-amber-600", label: "Bounced" },
  processing: { Icon: Loader2, cls: "text-muted-foreground animate-spin", label: "Processing" },
};

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const Row = ({ r }) => {
  const s = STYLE[r.status] || STYLE.skipped;
  return (
    <li data-testid={`inbox-log-row-${r.status}`} className="flex items-start gap-2 px-3 py-2 text-xs">
      <s.Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${s.cls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="truncate font-medium">{r.subject || "(no subject)"}</span>
          <span className="text-muted-foreground shrink-0 ml-auto">{when(r.received_at)}</span>
        </div>
        <div className="text-muted-foreground truncate">{r.from || "unknown sender"}{r.source && r.source !== "resend" ? ` · ${r.source}` : ""}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
          <span className={`font-mono uppercase tracking-wider text-[10px] ${s.cls}`} data-testid="inbox-log-outcome">{s.label}</span>
          {r.documents?.map((d) => d.deleted
            ? <span key={d.id} className="text-muted-foreground line-through" data-testid={`inbox-log-doc-${d.id}`}>{d.title}</span>
            : <Link key={d.id} to={`/vault?doc=${d.id}`} className="text-primary hover:underline truncate max-w-[220px]" data-testid={`inbox-log-doc-${d.id}`}>{d.title}</Link>)}
          {r.reason && <span className="text-muted-foreground" data-testid="inbox-log-reason">{r.reason}</span>}
        </div>
      </div>
    </li>
  );
};

// Last N emails that hit the forwarding inbox: what was filed, skipped or bounced.
export const InboxLog = ({ limit = 20, showCounts = true }) => {
  const { data, isLoading } = useQuery({ queryKey: ["inbox-log", limit], queryFn: () => inboundLog(limit), staleTime: 15_000 });
  if (isLoading) return <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading inbox…</div>;
  if (!data?.items?.length) return <div className="text-xs text-muted-foreground" data-testid="inbox-log-empty">Nothing has hit the inbox yet.</div>;
  return (
    <div data-testid="inbox-log" className="space-y-1.5">
      {showCounts && (
        <div className="flex gap-3 text-[11px] font-mono uppercase tracking-wider" data-testid="inbox-log-counts">
          <span className="text-emerald-600">{data.counts.filed} filed</span>
          <span className="text-stone-500">{data.counts.skipped} skipped</span>
          <span className="text-amber-600">{data.counts.bounced} bounced</span>
        </div>
      )}
      <ul className="rounded-xl border border-border divide-y divide-border">{data.items.map((r) => <Row key={r.email_id} r={r} />)}</ul>
    </div>
  );
};
